import * as ReactDOM from 'react-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Trans, useTranslation } from 'react-i18next'
import { Accordion, AccordionItem, Choice } from '@cmsgov/ds-medicare-gov'
import { PremiumsEasyPayAccountType } from '@cms-max/medicare-api-sdk'

import { MyChoiceList } from 'components/ReactHookForm/MyChoiceList'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import Button from 'components/Button'
import { IconChevronUp } from 'components/Icon/IconChevronUp'
import { IconChevronDown } from 'components/Icon/IconChevronDown'
import { EasyPayEditFormData, createSchema } from './schema'
import { IconInfoCircleOutline } from 'components/Icon/IconInfoCircleOutline'
import EasyPayHelpDrawer from '../EasyPay/EasyPayHelpDrawer/EasyPayHelpDrawer'
import LoadingButton from 'components/LoadingButton'

import classes from './EasyPayEditForm.module.scss'
import { useEvent } from 'hooks/useEvent'

const LinkText: React.FC = ({ children }) => (
  <span className="ds-c-link">{children}</span>
)

type EasyPayEditFormProps = {
  loading?: boolean
  pageType: 'easypaysignup' | 'easypaychangebank'
  onCancel?: () => void
  onSubmit: (formData: EasyPayEditFormData) => void
}
export function EasyPayEditForm ({
  loading,
  pageType,
  onCancel,
  onSubmit
}: EasyPayEditFormProps) {
  const { t } = useTranslation('easyPayCommon', { keyPrefix: 'editForm' })

  const [showHelpDrawer, setShowHelpDrawer] = useState(false)
  const [isControlledOpen, setIsControlledOpen] = useState<boolean>(false)
  const toggleHelpDrawer = () => setShowHelpDrawer((prev) => !prev)
  const { fireEvent } = useEvent('premiumsEasyPay')

  const schema = useMemo(() => createSchema(t), [t])
  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(schema)
  })
  const { control, handleSubmit } = methods
  const { errors } = methods.formState

  const termBullets = t('terms.bullets', { returnObjects: true }) as {
    text: string
    bullets?: string[]
  }[]

  const openCloseDrawerFireEvent = (isRouting: boolean) => {
    if (isRouting) {
      showHelpDrawer
        ? fireEvent(`premiums_${pageType}_routinginfo_draweropen`)
        : fireEvent(`premiums_${pageType}_routinginfo_drawerclose`)
    } else {
      showHelpDrawer
        ? fireEvent(`premiums_${pageType}_accountinfo_draweropen`)
        : fireEvent(`premiums_${pageType}_accountinfo_drawerclose`)
    }
  }

  const accordionOnChange = useCallback(() => {
    isControlledOpen
      ? fireEvent(`premiums_${pageType}_termsconditions_closeaccordion`)
      : fireEvent(`premiums_${pageType}_termsconditions_openaccordion`)
    setIsControlledOpen(!isControlledOpen)
  }, [isControlledOpen, setIsControlledOpen, fireEvent, pageType])

  // TODO — Remove this once DS is updated
  // This is fixed by https://github.com/CMSgov/design-system/pull/2271 in DS 5.0.1+
  const accordionRef = useRef<AccordionItem>(null)
  useEffect(() => {
    ReactDOM.findDOMNode(accordionRef.current)!
      // @ts-ignore
      .querySelector('button')
      .setAttribute('type', 'button')
  }, [])

  return (
    <FormProvider {...methods}>
      {showHelpDrawer && <EasyPayHelpDrawer onClose={toggleHelpDrawer} />}

      <form onSubmit={handleSubmit(onSubmit)}>
        <MyChoiceList
          variableInputName="accountType"
          label={<h2 className="ds-text-heading--xl">{t('accountType.heading')}</h2>}
          errorMessageString=""
          addErrorPrefix
          choices={[
            {
              label: t('accountType.checking'),
              value: PremiumsEasyPayAccountType.Checking
            },
            {
              label: t('accountType.savings'),
              value: PremiumsEasyPayAccountType.Savings
            }
          ]}
          layout="horizontal"
        />

        <MyTextField
          name="routing"
          label={t('routing.label')}
          addErrorPrefix
          autoComplete='off'
          hint={
            // TODO — add functionality to open drawer when help text is clicked
            <button
              type="button"
              className="ds-u-fill--transparent ds-u-border--0 ds-u-sans ds-u-font-size--sm ds-u-padding--0 my-cursor-pointer"
              onClick={() => {
                toggleHelpDrawer()
                openCloseDrawerFireEvent(true)
              }}
            >
              <div className="ds-u-display--inline-block ds-u-color--secondary ds-u-text-decoration--underline">
                {t('routing.help')}
              </div>{' '}
              <IconInfoCircleOutline
                className="ds-u-color--secondary ds-u-display--inline-block ds-u-valign--middle"
                style={{ width: '18px', height: '18px' }}
              />
            </button>
          }
        />

        <MyTextField
          name="accountNumber"
          label={t('accountNumber.label')}
          visibilityToggle
          addErrorPrefix
          autoComplete='off'
          hint={
            <button
              type="button"
              className="ds-u-fill--transparent ds-u-border--0 ds-u-sans ds-u-font-size--sm ds-u-padding--0 my-cursor-pointer"
              onClick={() => {
                toggleHelpDrawer()
                openCloseDrawerFireEvent(false)
              }}
            >
              <div className="ds-u-display--inline ds-u-color--secondary ds-u-text-decoration--underline">
                {t('accountNumber.help')}
              </div>{' '}
              <IconInfoCircleOutline
                className="ds-u-color--secondary ds-u-display--inline-block ds-u-valign--middle"
                style={{ width: '18px', height: '18px' }}
              />
            </button>
          }
        />

        <MyTextField
          name="verifyAccountNumber"
          label={t('verifyAccountNumber.label')}
          visibilityToggle
          addErrorPrefix
          autoComplete='off'
        />

        <div className="ds-u-margin-y--3">
          <Controller
            name="authorization"
            control={control}
            render={({ field }) => (
              <Choice
                type="checkbox"
                label={t('authorization.label')}
                errorMessage={errors?.authorization?.message}
                {...field}
              />
            )}
          />
        </div>

        <Accordion>
          <AccordionItem
            ref={accordionRef}
            onChange={accordionOnChange}
            isControlledOpen={isControlledOpen}
            heading={
              <div className="">
                <Trans
                  t={t}
                  i18nKey="terms.heading"
                  components={{
                    link1: <LinkText />
                  }}
                />
              </div>
            }
            openIcon={<IconChevronDown />}
            closeIcon={<IconChevronUp />}
            buttonClassName="ds-u-fill--transparent ds-u-font-weight--normal ds-u-font-weight--bold ds-u-padding--0 ds-u-margin-bottom--1"
            contentClassName={`ds-u-padding--0 ds-u-measure--wide ${classes.accordionContent}`}
          >
            <ul className="ds-c-list">
              {termBullets.map((item, idx) => (
                <li key={`terms-bullet${idx}`}>
                  {item.text}
                  {item.bullets && (
                    <ol>
                      {item.bullets.map((bullet, idx) => (
                        <li key={`terms-bullet${idx}`}>{bullet}</li>
                      ))}
                    </ol>
                  )}
                </li>
              ))}
            </ul>
          </AccordionItem>
        </Accordion>

        <hr className="ds-u-margin-top--3" />

        <div className="ds-u-margin-y--3">{t('printThisPage')}</div>

        <div className="ds-u-display--flex ds-u-flex-direction--column ds-u-md-flex-direction--row my-u-gap--12">
          <LoadingButton
            type="submit"
            className="ds-c-button ds-c-button--alternate ds-c-button--solid my-u-whitespace-nowrap"
            loading={loading}
            disabled={loading}
          >
            {t('buttons.submit')}
          </LoadingButton>

          {onCancel && (
            <Button
              type="button"
              onClick={(params) => {
                fireEvent(`premiums_${pageType}_cancel_click`, params)
                onCancel()
              }
            }
              className="ds-c-button my-u-whitespace-nowrap"
            >
              <span>{t('buttons.cancel')}</span>
            </Button>
          )}
        </div>
      </form>
    </FormProvider>
  )
}
